import React, { useState, useEffect } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Form, FormGroup, Label, Input, Button
} from 'reactstrap';
import {
  Box, Typography, CircularProgress, Alert, Snackbar, Stack, useMediaQuery,
  Paper, Divider, Autocomplete, TextField
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import {
  createBantuanLengkap, createBantuanManual, getTflList, getAgrList
} from '../API/bantuan';
import { getSupplierBarang } from '../API/supplierPenyedia';
import SourceDropdown from './modalAdd/SourceDropdown';
import { getRegisterUsulanByNik } from '../API/sourceDropdown';
import ManualBantuanFields from './modalAdd/ManualBantuanFields';
import ProsesBantuan from './ProsesBantuan';
import { formatNominalInput, parseNominalInput } from '../../utils/currency';
import { KECAMATAN, getDesaByKecamatan } from '../datarecap/constants';
import { toProtectedFileUrl } from '../../utils/secureFile';
import SecureFileImg from '../SecureFileImg';

const BASE_URL = process.env.REACT_APP_URL;

const JENIS_DISPLAY = [
  'Pembangunan Rumah Layak Huni', 'Rehabilitasi Rumah Tidak Layak Huni'
];
const DISPLAY_TO_BACKEND = {
  'Pembangunan Rumah Layak Huni': 'Pembangunan Rumah Layak Huni',
  'Rehabilitasi Rumah Tidak Layak Huni': 'Rehab Rumah Tidak Layak Huni',
};

// Default anggaran: Bangun Baru = material 40jt + upah 10jt
const DEFAULT_NOMINAL = {
  'Pembangunan Rumah Layak Huni': { nominalMaterial: 40000000, nominalUpahKerja: 10000000 },
  'Rehab Rumah Tidak Layak Huni': { nominalMaterial: 0, nominalUpahKerja: 0 },
};

const DetailRow = ({ label, value }) => (
  <div className="col-md-6 mb-2">
    <small className="text-muted d-block">{label}</small>
    <strong>{value || '-'}</strong>
  </div>
);

const buildMetadataFromSource = (source, sumberType) => {
  if (!source?.id) return { desil: '', kode: '', HasilVerfak: '' };
  const year = new Date().getFullYear();
  const prefix = sumberType === 'Questionnaire' ? 'Q' : sumberType === 'Verifikasi' ? 'V' : 'R';
  return {
    desil: source.score != null && source.score !== '' ? String(source.score) : '',
    kode: `${source.kecamatan || ''}·${source.desaKelurahan || ''}·${prefix}${source.id}|${year}`,
    HasilVerfak: source.kategori || source.statusVerifikasi || source.status || ''
  };
};

const TambahDataBantuan = () => {
  const navigate = useNavigate();
  const userRole = useSelector((s) => s.authAdmin?.user?.role);
  const userId = useSelector((s) => s.authAdmin?.user?.id);
  const isAdmin = userRole === 'admin';
  const isTfl = userRole === 'TFL';
  const isMobile = useMediaQuery('(max-width:800px)');

  const [tab, setTab] = useState('integrasi');
  const [saving, setSaving] = useState(false);
  const [tflList, setTflList] = useState([]);
  const [agrList, setAgrList] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [savedBantuanId, setSavedBantuanId] = useState(null);

  const [form, setForm] = useState({
    JenisBantuan: 'Pembangunan Rumah Layak Huni',
    nominal: 50000000,
    nominalMaterial: 40000000,
    nominalUpahKerja: 10000000,
    tanggalBantuan: new Date().toISOString().slice(0, 10),
    sumberType: '',
    QuestionnaireId: '', RegisterUsulanId: '', VerifikasiId: '',
    selQuestionnaire: null, selRegisterUsulan: null, selVerifikasi: null,
    sumberPreview: null,
    namaLengkap: '', nomorKK: '', nomorKTP: '', alamatLengkap: '',
    kecamatan: '', desaKelurahan: '',
    filterKecamatan: '', filterDesa: '',
    AdminId: isTfl ? String(userId || '') : '',
    AgrId: '', supplierId: '', supplierIds: [],
    agrFilterKecamatan: '', agrFilterDesa: '',
    statusKontrak: 'Sudah SK',
    noWaPenerimaBantuan: '', noRekening: '',
  });

  const totalNominal = (Number(form.nominalMaterial) || 0) + (Number(form.nominalUpahKerja) || 0);

  // AGR difilter berdasarkan kecamatan & desa/kelurahan wilayah AGR
  const filteredAgrList = agrList.filter((a) => {
    if (form.agrFilterKecamatan && a.kecamatan !== form.agrFilterKecamatan) return false;
    if (form.agrFilterDesa && a.desaKelurahan !== form.agrFilterDesa) return false;
    return true;
  });

  useEffect(() => {
    (async () => {
      try {
        const [tflRes, agrRes, supRes] = await Promise.all([
          getTflList(),
          getAgrList(),
          getSupplierBarang({ limit: 200 }),
        ]);
        setTflList(tflRes.data?.data || []);
        setAgrList(agrRes.data?.data || agrRes.data || []);
        setSupplierList(supRes.data?.data || supRes.data || []);
      } catch (e) {
        console.error('Load lists:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const applySourceSelection = (sumberType, source) => {
    const meta = buildMetadataFromSource(source, sumberType);
    setForm((p) => ({
      ...p,
      sumberType,
      sumberPreview: source,
      registerUsulanInfo: null,
      RegisterUsulanId: '',
      desil: meta.desil,
      kode: meta.kode,
      HasilVerfak: meta.HasilVerfak
    }));

    // Auto-lookup register usulan by NIK dari sumber yang dipilih
    if (source?.nik || source?.nomorKTP) {
      const nik = String(source.nik || source.nomorKTP || '').trim();
      if (nik.length >= 8) {
        getRegisterUsulanByNik(nik)
          .then((res) => {
            const data = res.data?.data;
            if (data) {
              setForm((p) => ({ ...p, registerUsulanInfo: data, RegisterUsulanId: data.id }));
            }
          })
          .catch(() => {});
      }
    }
  };

  const handleSave = async () => {
    if (!form.JenisBantuan) return setSnackbar({ open: true, message: 'Jenis bantuan wajib diisi', severity: 'warning' });

    if (tab === 'manual') {
      if (!form.namaLengkap) return setSnackbar({ open: true, message: 'Nama lengkap wajib diisi', severity: 'warning' });
      setSaving(true);
      try {
        const res = await createBantuanManual({
          namaLengkap: form.namaLengkap, nomorKK: form.nomorKK, nomorKTP: form.nomorKTP,
          alamatLengkap: form.alamatLengkap, kecamatan: form.kecamatan, desaKelurahan: form.desaKelurahan,
          JenisBantuan: form.JenisBantuan,
          nominal: totalNominal,
          nominalMaterial: Number(form.nominalMaterial) || 0,
          nominalUpahKerja: Number(form.nominalUpahKerja) || 0,
          AdminId: form.AdminId || null, AgrId: form.AgrId || null,
          supplierId: form.supplierId || null, SupplierBarangIds: form.supplierIds || [],
          statusKontrak: form.statusKontrak,
          tanggalBantuan: form.tanggalBantuan,
          noWaPenerimaBantuan: form.noWaPenerimaBantuan || '', noRekening: form.noRekening || '',
        });
        const bantuanId = res.data?.bantuan?.id || res.data?.data?.id;
        setSnackbar({ open: true, message: 'Bantuan berhasil disimpan. Lanjut isi kelengkapan dokumen.', severity: 'success' });
        if (bantuanId) setSavedBantuanId(bantuanId);
      } catch (err) {
        setSnackbar({ open: true, message: err.response?.data?.msg || 'Gagal menyimpan', severity: 'error' });
      } finally { setSaving(false); }
      return;
    }

    const payload = {
      JenisBantuan: form.JenisBantuan,
      nominal: totalNominal,
      nominalMaterial: Number(form.nominalMaterial) || 0,
      nominalUpahKerja: Number(form.nominalUpahKerja) || 0,
      tanggalBantuan: form.tanggalBantuan || null,
      AdminId: form.AdminId || null, AgrId: form.AgrId || null,
      supplierId: form.supplierId || null, SupplierBarangIds: form.supplierIds || [],
      noWaPenerimaBantuan: form.noWaPenerimaBantuan || '',
      noRekening: form.noRekening || '',
    };
    if (form.sumberType === 'Questionnaire') {
      payload.QuestionnaireId = form.selQuestionnaire?.id || null;
      payload.VerifikasiId = null;
      // Auto-link register usulan jika ditemukan via NIK
      payload.RegisterUsulanId = form.RegisterUsulanId || null;
    } else if (form.sumberType === 'Verifikasi') {
      payload.VerifikasiId = form.selVerifikasi?.id || null;
      payload.QuestionnaireId = null;
      // Auto-link register usulan jika ditemukan via NIK
      payload.RegisterUsulanId = form.RegisterUsulanId || null;
    } else {
      payload.QuestionnaireId = null; payload.RegisterUsulanId = null; payload.VerifikasiId = null;
    }

    setSaving(true);
    try {
      const res = await createBantuanLengkap(payload);
      const bantuanId = res.data?.bantuan?.id || res.data?.data?.id;
      const warnings = res.data?.warnings || [];
      const warnText = warnings.length ? ` Peringatan: ${warnings.join(' ')}` : '';

      setSnackbar({
        open: true,
        message: `Bantuan berhasil disimpan. Lanjut isi kelengkapan dokumen di Proses Bantuan.${warnText}`,
        severity: warnings.length ? 'warning' : 'success',
      });
      if (bantuanId) setSavedBantuanId(bantuanId);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.msg || err.response?.data?.message || 'Gagal menyimpan', severity: 'error' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  return (
    <div className="main-content" style={contentStyle}>
     
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Button color="primary" size="sm" onClick={() => navigate('/data-bantuan')}
            style={{ backgroundColor: '#1976d2', color: '#fff', borderColor: '#1976d2' }}>
            <ArrowBack fontSize="small" sx={{ mr: 0.5 }} /> Kembali
          </Button>
          <Typography variant="h5" fontWeight={600}>Tambah Data Penerima Bantuan</Typography>
        </Box>

        {!savedBantuanId ? (
        <Box sx={{ bgcolor: 'white', borderRadius: 2, p: { xs: 2, sm: 3 }, boxShadow: 1 }}>
         
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">Jenis Bantuan</Label>
                  <Input type="select" value={form.JenisBantuan}
                    onChange={(e) => {
                      const jenis = e.target.value;
                      const def = DEFAULT_NOMINAL[jenis] || { nominalMaterial: 0, nominalUpahKerja: 0 };
                      setForm((p) => ({
                        ...p,
                        JenisBantuan: jenis,
                        nominalMaterial: def.nominalMaterial,
                        nominalUpahKerja: def.nominalUpahKerja,
                        nominal: def.nominalMaterial + def.nominalUpahKerja,
                      }));
                    }}>
                    {JENIS_DISPLAY.map((j) => {
                      const backendVal = DISPLAY_TO_BACKEND[j];
                      return <option key={j} value={backendVal}>{j}</option>;
                    })}
                  </Input>
                  {/pembangunan/i.test(form.JenisBantuan) && (
                    <small className="text-muted">Default Bangun Baru: Material Rp 40.000.000 + Upah Rp 10.000.000</small>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">Nominal Total (Rp)</Label>
                  <Input type="text" readOnly
                    value={formatNominalInput(totalNominal)}
                    style={{ backgroundColor: '#f5f5f5', fontWeight: 600 }} />
                  <small className="text-muted">Otomatis dari Nominal Material + Nominal Upah Kerja</small>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label className="fw-semibold">Nominal Material (Rp)</Label>
                  <Input type="text" inputMode="numeric" placeholder="0"
                    value={formatNominalInput(form.nominalMaterial)}
                    onChange={(e) => setForm((p) => ({ ...p, nominalMaterial: parseNominalInput(e.target.value) }))} />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className="fw-semibold">Nominal Upah Kerja (Rp)</Label>
                  <Input type="text" inputMode="numeric" placeholder="0"
                    value={formatNominalInput(form.nominalUpahKerja)}
                    onChange={(e) => setForm((p) => ({ ...p, nominalUpahKerja: parseNominalInput(e.target.value) }))} />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className="fw-semibold">Total Material + Upah (Rp)</Label>
                  <Input type="text" readOnly
                    value={formatNominalInput(totalNominal)}
                    style={{ backgroundColor: '#f5f5f5', fontWeight: 600 }} />
                </FormGroup>
              </Col>
            </Row>

            {/* === Sumber Data & Penerima Bantuan (di atas) === */}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">Tanggal Input Bantuan</Label>
                  <Input type="date" value={form.tanggalBantuan}
                    onChange={(e) => setForm((p) => ({ ...p, tanggalBantuan: e.target.value }))} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">Sumber Data Penerima Bantuan</Label>
                  <Input type="select" value={form.sumberType}
                    onChange={(e) => setForm((p) => ({
                      ...p, sumberType: e.target.value,
                      selQuestionnaire: null, selRegisterUsulan: null, selVerifikasi: null,
                      sumberPreview: null,
                      registerUsulanInfo: null,
                      RegisterUsulanId: '',
                      filterKecamatan: '', filterDesa: '',
                    }))}>
                    <option value="">(Tanpa sumber)</option>
                    <option value="Questionnaire">Verifikasi Rehab RTLH</option>
                    <option value="Verifikasi">Verifikasi Pembangunan RLH</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {form.sumberType && (
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label className="fw-semibold">Filter Kecamatan</Label>
                    <Input type="select" value={form.filterKecamatan || ''}
                      onChange={(e) => setForm((p) => ({
                        ...p,
                        filterKecamatan: e.target.value,
                        filterDesa: '',
                        selQuestionnaire: null, selRegisterUsulan: null, selVerifikasi: null,
                        sumberPreview: null,
                        registerUsulanInfo: null,
                        QuestionnaireId: '', RegisterUsulanId: '', VerifikasiId: '',
                      }))}>
                      <option value="">Semua Kecamatan</option>
                      {KECAMATAN.map((k) => <option key={k} value={k}>{k}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="fw-semibold">Filter Desa/Kelurahan</Label>
                    <Input type="select" value={form.filterDesa || ''}
                      disabled={!form.filterKecamatan}
                      onChange={(e) => setForm((p) => ({
                        ...p,
                        filterDesa: e.target.value,
                        selQuestionnaire: null, selRegisterUsulan: null, selVerifikasi: null,
                        sumberPreview: null,
                        registerUsulanInfo: null,
                        QuestionnaireId: '', RegisterUsulanId: '', VerifikasiId: '',
                      }))}>
                      <option value="">{form.filterKecamatan ? 'Semua Desa/Kelurahan' : 'Pilih kecamatan dulu'}</option>
                      {getDesaByKecamatan(form.filterKecamatan).map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
            )}

            {form.sumberType === 'Questionnaire' && (
              <FormGroup>
                <Label>Pilih Verifikasi Rehab RTLH (cari & scroll)</Label>
                <SourceDropdown type="questionnaire" value={form.selQuestionnaire}
                  kecamatan={form.filterKecamatan}
                  desaKelurahan={form.filterDesa}
                  onChange={(val) => { setForm((p) => ({ ...p, selQuestionnaire: val, QuestionnaireId: val?.id || '' })); applySourceSelection('Questionnaire', val); }} />
              </FormGroup>
            )}
            {form.sumberType === 'Verifikasi' && (
              <FormGroup>
                <Label>Pilih Verifikasi Pembangunan RLH (cari & scroll)</Label>
                <SourceDropdown type="verifikasi" value={form.selVerifikasi}
                  kecamatan={form.filterKecamatan}
                  desaKelurahan={form.filterDesa}
                  onChange={(val) => { setForm((p) => ({ ...p, selVerifikasi: val, VerifikasiId: val?.id || '' })); applySourceSelection('Verifikasi', val); }} />
              </FormGroup>
            )}

            {/* === No WA & Rekening Penerima === */}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">No. WA Penerima</Label>
                  <Input type='number' placeholder="Contoh: 081234567890" value={form.noWaPenerimaBantuan || ''}
                    onChange={(e) => setForm((p) => ({ ...p, noWaPenerimaBantuan: e.target.value }))} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">No. Rekening</Label>
                  <Input type='number' placeholder="Contoh: 1234567890" value={form.noRekening || ''}
                    onChange={(e) => setForm((p) => ({ ...p, noRekening: e.target.value }))} />
                </FormGroup>
              </Col>
            </Row>

            {/* === TFL, AGR, Supplier (dipindah ke bawah) === */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Penunjukan Pendamping & Supplier</Typography>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label className="fw-semibold">TFL Pendamping</Label>
                  <Input type="select" value={form.AdminId || ''} disabled={isTfl}
                    onChange={(e) => setForm((p) => ({ ...p, AdminId: e.target.value }))}>
                    {isTfl ? (
                      tflList.filter(t => t.id === userId).length > 0 ? (
                        tflList.filter(t => t.id === userId).map((t) => <option key={t.id} value={t.id}>{t.username} — {t.NoWa || '-'}</option>)
                      ) : (
                        <option value={String(userId)}>TFL (Anda)</option>
                      )
                    ) : (
                      <>
                        <option value="">(Pilih TFL)</option>
                        {tflList.map((t) => <option key={t.id} value={t.id}>{t.username} — {t.NoWa || '-'}</option>)}
                      </>
                    )}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className="fw-semibold">AGR Peliuk</Label>
                  <Row className="g-1 mb-1">
                    <Col xs={6}>
                      <Input type="select" bsSize="sm" value={form.agrFilterKecamatan || ''}
                        onChange={(e) => setForm((p) => ({
                          ...p,
                          agrFilterKecamatan: e.target.value,
                          agrFilterDesa: '',
                          AgrId: '',
                        }))}>
                        <option value="">Semua Kecamatan</option>
                        {KECAMATAN.map((k) => <option key={k} value={k}>{k}</option>)}
                      </Input>
                    </Col>
                    <Col xs={6}>
                      <Input type="select" bsSize="sm" value={form.agrFilterDesa || ''}
                        disabled={!form.agrFilterKecamatan}
                        onChange={(e) => setForm((p) => ({
                          ...p,
                          agrFilterDesa: e.target.value,
                          AgrId: '',
                        }))}>
                        <option value="">{form.agrFilterKecamatan ? 'Semua Desa/Kel.' : 'Pilih kecamatan dulu'}</option>
                        {getDesaByKecamatan(form.agrFilterKecamatan).map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </Input>
                    </Col>
                  </Row>
                  <Autocomplete
                    size="small"
                    options={filteredAgrList}
                    value={filteredAgrList.find((a) => String(a.id) === String(form.AgrId)) || null}
                    onChange={(_, val) => setForm((p) => ({ ...p, AgrId: val?.id ? String(val.id) : '' }))}
                    getOptionLabel={(opt) => opt ? `${opt.username || '-'} — ${opt.NoWa || '-'}${opt.desaKelurahan ? ` (${opt.desaKelurahan})` : ''}` : ''}
                    isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                    filterOptions={(options, { inputValue }) => {
                      const q = (inputValue || '').toLowerCase().trim();
                      if (!q) return options;
                      return options.filter((a) =>
                        String(a.username || '').toLowerCase().includes(q)
                        || String(a.NoWa || '').toLowerCase().includes(q)
                        || String(a.desaKelurahan || '').toLowerCase().includes(q)
                        || String(a.kecamatan || '').toLowerCase().includes(q)
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Cari AGR (nama / No. WA / desa)..."
                        variant="outlined"
                        size="small"
                      />
                    )}
                    noOptionsText={form.agrFilterKecamatan || form.agrFilterDesa ? 'Tidak ada AGR di wilayah ini' : 'AGR tidak ditemukan'}
                    clearOnEscape
                  />
                  {(form.agrFilterKecamatan || form.agrFilterDesa) && (
                    <small className="text-muted">
                      Menampilkan {filteredAgrList.length} AGR
                      {form.agrFilterDesa ? ` di Desa/Kel. ${form.agrFilterDesa}` : form.agrFilterKecamatan ? ` di Kec. ${form.agrFilterKecamatan}` : ''}
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className="fw-semibold">Toko Supplier (bisa pilih lebih dari 1)</Label>
                  <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 6, padding: '4px 8px' }}>
                    {supplierList.map((s) => {
                      const checked = (form.supplierIds || []).includes(s.id);
                      return (
                        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                          <input type="checkbox" id={`sup_${s.id}`} checked={checked} style={{ margin: 0 }}
                            onChange={(e) => setForm((p) => ({
                              ...p,
                              supplierIds: e.target.checked ? [...(p.supplierIds || []), s.id] : (p.supplierIds || []).filter(id => id !== s.id),
                              supplierId: e.target.checked && !p.supplierId ? s.id : p.supplierId,
                            }))} />
                          <label htmlFor={`sup_${s.id}`} style={{ fontSize: 13, cursor: 'pointer', margin: 0 }}>
                            {s.namaToko || s.namaSupplier || `Toko #${s.id}`}
                          </label>
                        </div>
                      );
                    })}
                    {supplierList.length === 0 && <small className="text-muted">Belum ada toko tersedia</small>}
                  </div>
                </FormGroup>
              </Col>
            </Row>

            {/* === Data Diri Terpilih (paling bawah) === */}
            {form.sumberPreview && (
              <div className="border rounded p-3 mb-3 bg-light" style={{ mt: 3 }}>
                <h6 className="text-muted mb-2">Data Diri Terpilih</h6>
                <div className="row">
                  <DetailRow label="Nama" value={form.sumberPreview.nama || form.sumberPreview.namaLengkapKK || form.sumberPreview.namaKepalaKeluarga} />
                  <DetailRow label="NIK" value={form.sumberPreview.nik || form.sumberPreview.nomorKTP} />
                  <DetailRow label="Nomor KK" value={form.sumberPreview.nomorKK} />
                  <DetailRow label="Usia" value={form.sumberPreview.usia ? `${form.sumberPreview.usia} Tahun` : '-'} />
                  <DetailRow label="Tanggal Lahir" value={form.sumberPreview.tanggallahir} />
                  <DetailRow label="Jenis Kelamin" value={form.sumberPreview.jenisKelamin} />
                  <DetailRow label="Alamat" value={form.sumberPreview.alamat || form.sumberPreview.alamatRumah} />
                  <DetailRow label="Kecamatan" value={form.sumberPreview.kecamatan} />
                  <DetailRow label="Desa/Kelurahan" value={form.sumberPreview.desaKelurahan} />
                  <DetailRow label="Pekerjaan" value={form.sumberPreview.pekerjaan} />
                  <DetailRow label="Penghasilan" value={form.sumberPreview.penghasilan != null ? `Rp ${Number(form.sumberPreview.penghasilan).toLocaleString('id-ID')}` : '-'} />
                  <DetailRow label="Pendidikan" value={form.sumberPreview.pendidikanTerakhir} />
                  <DetailRow label="Status Kepemilikan Rumah" value={form.sumberPreview.statusKepemilikanRumah} />
                  <DetailRow label="Jumlah Penghuni" value={form.sumberPreview.jumlahPenghuni} />
                  <DetailRow label="Luas Rumah" value={form.sumberPreview.luasRumah ? `${form.sumberPreview.luasRumah} m²` : '-'} />
                  <DetailRow label="Model Rumah" value={form.sumberPreview.modelRumah} />
                  <DetailRow label="Kategori / Status" value={form.sumberPreview.kategori || form.sumberPreview.statusVerifikasi || form.sumberPreview.status} />
                  <DetailRow label="Skor" value={form.sumberPreview.score} />
                  <DetailRow label="Tanggal Pendataan" value={form.sumberPreview.tanggalPendataan} />
                  <DetailRow label="Status Validasi Bantuan" value={form.sumberPreview.verifikasiBantuan} />
                  <DetailRow label="Catatan" value={form.sumberPreview.catatan} />

                  {/* Auto-lookup register usulan by NIK */}
                  {form.registerUsulanInfo && (
                    <>
                      <div className="col-12 mt-2">
                        <small className="text-muted d-block">Data Register Usulan (auto-deteksi via NIK)</small>
                      </div>
                      <DetailRow label="Nomor Surat" value={form.registerUsulanInfo.nomorSurat} />
                      <DetailRow label="Tanggal Surat" value={form.registerUsulanInfo.tanggalSurat} />
                      <DetailRow label="Disposisi Dari" value={form.registerUsulanInfo.disposisiDari} />
                      <DetailRow label="Diteruskan Kepada" value={form.registerUsulanInfo.diteruskanKepada} />
                      <DetailRow label="Perihal / Usulan" value={form.registerUsulanInfo.perihal} />
                    </>
                  )}
                </div>
                {/* Galeri foto dari Questionnaire (fotos[]) atau Verifikasi (fotoRumahSaatIni/foto/fotoKK/...) */}
                {(() => {
                  const preview = form.sumberPreview;
                  const gallery = Array.isArray(preview.fotos) && preview.fotos.length > 0
                    ? preview.fotos
                    : [
                        preview.fotoRumahSaatIni && { label: 'Foto Rumah Saat Ini', foto_rumah: preview.fotoRumahSaatIni },
                        preview.foto && { label: 'Foto Rumah', foto_rumah: preview.foto },
                        preview.fotoKK && { label: 'Foto KK', foto_rumah: preview.fotoKK },
                        preview.fotoSuratKepemilikanTanah && { label: 'Surat Kepemilikan Tanah', foto_rumah: preview.fotoSuratKepemilikanTanah },
                      ].filter(Boolean);
                  if (!gallery.length) return null;
                  return (
                    <div className="mt-2">
                      <small className="text-muted d-block mb-1">
                        Foto / Dokumen Sumber ({gallery.length})
                        {preview.sourceType === 'verifikasi' || preview.sourceName === 'Validasi' ? ' — Verifikasi' : preview.sourceType === 'questionnaire' ? ' — Questionnaire' : ''}
                      </small>
                      <div className="d-flex gap-2 flex-wrap">
                        {gallery.map((f, fi) => {
                          const path = f.foto_rumah || f;
                          const url = toProtectedFileUrl(path);
                          return (
                            <div key={fi} style={{ textAlign: 'center', maxWidth: 100 }}>
                              <SecureFileImg
                                src={url}
                                alt={f.label || `Foto ${fi + 1}`}
                                enablePreview={false}
                                style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer', display: 'block' }}
                                onClick={() => openInAppFilePreview({ url, title: f.label || `Foto ${fi + 1}` })}
                              />
                              <small className="text-muted d-block mt-1" style={{ fontSize: 10, lineHeight: 1.2 }}>
                                {f.label || `Foto ${fi + 1}`}
                              </small>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <Divider sx={{ my: 2 }} />
            <Paper sx={{ p: 2, bgcolor: '#e3f2fd', mb: 2 }}>
              <Typography variant="body2" color="primary.main">
                Setelah data bantuan disimpan, Anda akan diarahkan untuk mengupload kelengkapan dokumen administrasi (ADM). Setiap langkah dapat di-save dokumen secara terpisah dengan preview file dan foto.
              </Typography>
            </Paper>
         

          <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
            <Button color="secondary" onClick={() => navigate('/data-bantuan')} block={isMobile}>
              Batal
            </Button>
            <Button color="success" disabled={saving} onClick={handleSave} block={isMobile}>
              <Save fontSize="small" sx={{ mr: 0.5 }} />
              {saving ? 'Menyimpan...' : 'Simpan Data Bantuan'}
            </Button>
          </Box>
        </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Data penerima bantuan berhasil disimpan. Lengkapi berkas ADM pada setiap langkah di bawah ini. Setiap langkah dapat disimpan secara terpisah.
            </Alert>
            <ProsesBantuan
              inline
              admOnly
              embedded
              inlineBantuanId={savedBantuanId}
              inlineBackPath="/data-bantuan"
              userRole={userRole}
            />
          </Box>
        )}

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
    
    </div>
  );
};

export default TambahDataBantuan;
